// Medicine labels (FR-DSP-4). Printed on 50x30mm label stock or an 80mm roll.
// The patient reads this at home, so the instructions are in Burmese by default.
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { useDosagePhrases } from '@/features/consult/pickers';
import { formatDate, toMyanmarDigits } from '@/lib/myanmar/digits';

export interface LabelItem {
  drug_name: string;
  qty: number;
  dose_qty: number | null;
  dose_unit: string | null;
  frequency_code: string | null;
  instruction_codes: string[];
  free_text?: string | null;
}

export function MedicineLabels({ items, patientName, patientNo, clinicName, onClose }: {
  items: LabelItem[]; patientName: string; patientNo: string | null; clinicName: string; onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const freqs = useDosagePhrases('frequency');
  const units = useDosagePhrases('dose_unit');
  const instructions = useDosagePhrases('instruction');

  const phrase = (list: { code: string; text_en: string; text_my: string }[], code: string | null | undefined) => {
    const p = list.find((x) => x.code === code);
    return p ? (my ? p.text_my : p.text_en) : '';
  };
  const n = (v: number | string) => (my ? toMyanmarDigits(v) : String(v));

  return (
    <div className="grid gap-4">
      <div className="labels grid gap-2">
        {items.map((it, i) => (
          <div key={i} className="label mx-auto w-full max-w-xs border border-dashed border-rule bg-white p-3 text-ink">
            <div className="truncate text-[10px] text-ink/60">{clinicName}</div>
            <div className="truncate text-sm font-bold leading-tight">{it.drug_name}</div>
            <div className="text-[13px] font-semibold leading-snug">
              {it.dose_qty ? `${n(it.dose_qty)} ${phrase(units, it.dose_unit)} ` : ''}{phrase(freqs, it.frequency_code)}
            </div>
            {it.instruction_codes.length > 0 && (
              <div className="text-[12px] leading-snug">{it.instruction_codes.map((c) => phrase(instructions, c)).join(' · ')}</div>
            )}
            {it.free_text && <div className="text-[11px] italic leading-snug">{it.free_text}</div>}
            <div className="mt-1 flex justify-between text-[10px] text-ink/70">
              <span className="truncate">{patientName}{patientNo ? ` · ${patientNo}` : ''}</span>
              <span>{n(it.qty)} · {formatDate(new Date(), my ? 'my' : 'en')}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="no-print flex justify-end gap-2">
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button variant="primary" onClick={() => window.print()}>{t('common.print')}</Button>
      </div>
    </div>
  );
}
